'use client'

import {
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useId, useMemo, useRef, useState } from 'react'

type TaskWithSequence = {
  sequence?: number
}

type UseTaskDragDropParams<T extends TaskWithSequence> = {
  tasks: T[]
  onReorder: (reorderedTasks: T[]) => void
}

export function useTaskDragDrop<T extends TaskWithSequence>({
  tasks,
  onReorder,
}: UseTaskDragDropParams<T>) {
  const dndContextId = useId()
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const idCounterRef = useRef(0)
  const sequenceToIdMapRef = useRef<Map<number, string>>(new Map())

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Gera IDs estáveis baseados na sequence de cada task
  const itemIds = useMemo(() => {
    return tasks.map((task, index) => {
      const sequence = task.sequence ?? index

      if (!sequenceToIdMapRef.current.has(sequence)) {
        idCounterRef.current += 1
        sequenceToIdMapRef.current.set(
          sequence,
          `dnd-task-${idCounterRef.current}`,
        )
      }

      return sequenceToIdMapRef.current.get(sequence) ?? ''
    })
  }, [tasks])

  const activeItem = activeIndex !== null ? tasks[activeIndex] : null

  function handleDragStart(event: DragStartEvent) {
    const activeId = event.active.id as string
    const index = itemIds.indexOf(activeId)
    setActiveIndex(index >= 0 ? index : null)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveIndex(null)

    if (!over || active.id === over.id) return

    const oldIndex = itemIds.indexOf(active.id as string)
    const newIndex = itemIds.indexOf(over.id as string)

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

    // Reordena as tasks e atualiza as sequences
    const reorderedTasks = arrayMove([...tasks], oldIndex, newIndex).map(
      (task, index) => ({
        ...task,
        sequence: index,
      }),
    )

    // Limpa o mapa de IDs para regenerar com as novas sequences
    sequenceToIdMapRef.current.clear()

    onReorder(reorderedTasks)
  }

  return {
    dndContextId,
    sensors,
    itemIds,
    activeIndex,
    activeItem,
    handleDragStart,
    handleDragEnd,
  }
}
